import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ChartBarSquareIcon,
  CpuChipIcon,
  DevicePhoneMobileIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  PresentationChartLineIcon,
  BuildingOffice2Icon,
  ChatBubbleBottomCenterTextIcon,
  Squares2X2Icon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  CubeIcon,
  StarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline'
import { Text } from '../ui'
import { QgNumerosRow } from '../../types/qgNumeros'
import DashboardSectionHeader from './DashboardSectionHeader'
import { DashboardCardSkeleton } from './DashboardSkeleton'
import clsx from 'clsx'

const numberFormatter = new Intl.NumberFormat('pt-BR')
const AUTO_ADVANCE_MS = 6000

interface KpiItem {
  label: string
  value: number
  icon: React.ReactNode
}

interface KpiSection {
  id: string
  title: string
  icon: React.ReactNode
  items: KpiItem[]
}

const tabClass =
  'text-primary-600 dark:text-primary-300 border-primary-200 dark:border-primary-700/50 hover:bg-primary-50 dark:hover:bg-primary-900/30 data-[active=true]:bg-primary-500 data-[active=true]:text-white data-[active=true]:border-primary-500'

const kpiTileClass =
  'border-primary-500/20 bg-gradient-to-br from-primary-50/80 to-white dark:from-primary-900/30 dark:to-gray-800/80'

const kpiIconClass = 'bg-primary-50 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300'

const kpiValueClass = 'text-primary-600 dark:text-primary-300'

const cardSurface =
  'rounded-xl border border-primary-500/20 bg-gradient-to-br from-primary-50/50 via-white to-white dark:from-primary-900/30 dark:via-gray-800/90 dark:to-gray-800 shadow-sm ring-1 ring-primary-500/10 dark:ring-primary-400/20'

const informativoPanelClass =
  'rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800'

const AnimatedValue: React.FC<{ value: number; active: boolean }> = ({ value, active }) => {
  const [display, setDisplay] = useState(0)

  useEffect(() => {
    if (!active) {
      setDisplay(0)
      return
    }

    const duration = 700
    const start = performance.now()
    const from = 0
    let frame: number

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplay(Math.round(from + (value - from) * eased))
      if (progress < 1) frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [value, active])

  return <>{numberFormatter.format(display)}</>
}

interface KpiListItemProps {
  item: KpiItem
  animate: boolean
}

const KpiListItem: React.FC<KpiListItemProps & { className?: string }> = ({ item, animate, className }) => (
  <div className={clsx('min-w-0', className)}>
    <div className="flex items-center gap-2.5 sm:gap-3 px-3 py-2.5 h-full">
      <span className="flex-shrink-0 flex items-center justify-center rounded-lg w-11 h-11 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 [&>svg]:h-5 [&>svg]:w-5">
        {item.icon}
      </span>
      <span className="min-w-0 flex-1">
        <Text size="sm" weight="semibold" className="text-gray-900 dark:text-white line-clamp-2">
          {item.label}
        </Text>
        <Text size="xs" className="text-gray-600 dark:text-gray-300 tabular-nums">
          <AnimatedValue value={item.value} active={animate} />
        </Text>
      </span>
    </div>
  </div>
)

interface QGNumerosEmbeddedAllProps {
  sections: KpiSection[]
  footer?: React.ReactNode
}

const QGNumerosEmbeddedAll: React.FC<QGNumerosEmbeddedAllProps> = ({ sections, footer }) => (
  <div className={informativoPanelClass}>
    <ul className="divide-y divide-gray-200 dark:divide-gray-600">
      {sections.map((section) => (
        <React.Fragment key={section.id}>
          <li className="px-3 py-2 bg-gray-50/80 dark:bg-gray-800/80">
            <div className="flex items-center gap-1.5">
              <span className="flex-shrink-0 text-gray-500 dark:text-gray-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                {section.icon}
              </span>
              <Text size="xs" className="font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {section.title}
              </Text>
            </div>
          </li>
          <li>
            <div className="grid grid-cols-2 divide-y divide-gray-200 dark:divide-gray-600">
              {section.items.map((item) => (
                <KpiListItem
                  key={item.label}
                  item={item}
                  animate
                  className={section.items.length === 1 ? 'col-span-2' : undefined}
                />
              ))}
            </div>
          </li>
        </React.Fragment>
      ))}
    </ul>
    {footer}
  </div>
)

const KpiTile: React.FC<{ item: KpiItem; animate: boolean }> = ({ item, animate }) => (
  <div
      className={clsx(
        'group rounded-xl border p-4 sm:p-5 flex items-center gap-4 min-h-[6rem]',
        'shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5',
        kpiTileClass
      )}
    >
      <div
        className={clsx(
          'flex-shrink-0 h-11 w-11 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center transition-transform duration-200 group-hover:scale-105',
          kpiIconClass
        )}
      >
        {item.icon}
      </div>
      <div className="min-w-0 flex-1">
        <Text size="xs" color="muted" className="font-medium leading-snug line-clamp-2">
          {item.label}
        </Text>
        <p
          className={clsx(
            'text-2xl sm:text-3xl font-bold truncate tabular-nums tracking-tight mt-1',
            kpiValueClass
          )}
        >
          <AnimatedValue value={item.value} active={animate} />
        </p>
      </div>
    </div>
)

function buildSections(data: QgNumerosRow): KpiSection[] {
  return [
    {
      id: 'atendimentos',
      title: 'Atendimentos',
      icon: <CpuChipIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Analisados pela IA',
          value: data.total_ai_ticket_analyses,
          icon: <CpuChipIcon className="h-6 w-6" />,
        },
        {
          label: 'Importados do WhatsApp',
          value: data.total_whatsapp_tickets,
          icon: <DevicePhoneMobileIcon className="h-6 w-6" />,
        },
      ],
    },
    {
      id: 'qualidade',
      title: 'Qualidade',
      icon: <ClipboardDocumentCheckIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Tickets de qualidade',
          value: data.total_quality_tickets ?? 0,
          icon: <ClipboardDocumentCheckIcon className="h-6 w-6" />,
        },
        {
          label: 'Tickets de melhoria',
          value: data.total_quality_improvement_tickets ?? 0,
          icon: <LightBulbIcon className="h-6 w-6" />,
        },
      ],
    },
    {
      id: 'cct',
      title: 'Convenções',
      icon: <MagnifyingGlassIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Buscas de CCTs realizadas',
          value: data.total_cct_mte_searches,
          icon: <MagnifyingGlassIcon className="h-6 w-6" />,
        },
        {
          label: 'CCTs importadas para o SCGP',
          value: data.total_ccts_imported_scgp,
          icon: <ArrowDownTrayIcon className="h-6 w-6" />,
        },
      ],
    },
    {
      id: 'dashboards',
      title: 'Dashboards',
      icon: <PresentationChartLineIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Acessos ao Dash de Campanhas',
          value: data.total_campaign_dashboard_accesses,
          icon: <PresentationChartLineIcon className="h-6 w-6" />,
        },
        {
          label: 'Acessos ao Dash do Cliente',
          value: data.total_client_dashboard_accesses,
          icon: <BuildingOffice2Icon className="h-6 w-6" />,
        },
      ],
    },
    {
      id: 'pasigpt',
      title: 'PasiGPT',
      icon: <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Interações no PasiGPT',
          value: data.total_pasigpt_interactions,
          icon: <ChatBubbleBottomCenterTextIcon className="h-6 w-6" />,
        },
      ],
    },
    {
      id: 'superapp',
      title: 'SuperAPP',
      icon: <Squares2X2Icon className="h-4 w-4" />,
      items: [
        {
          label: 'Usuários do SuperAPP',
          value: data.total_superapp_users ?? 0,
          icon: <Squares2X2Icon className="h-6 w-6" />,
        },
      ],
    },
    {
      id: 'shop',
      title: 'Pasi Shop',
      icon: <ShoppingBagIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Acessos ao Pasi Shop',
          value: data.total_shop_accesses,
          icon: <ShoppingBagIcon className="h-6 w-6" />,
        },
        {
          label: 'Número de pedidos',
          value: data.total_shop_orders ?? 0,
          icon: <ShoppingCartIcon className="h-6 w-6" />,
        },
        {
          label: 'Produtos trocados',
          value: data.total_shop_order_items_quantity ?? 0,
          icon: <CubeIcon className="h-6 w-6" />,
        },
        {
          label: 'PASI Points trocados',
          value: data.total_shop_points_spent ?? 0,
          icon: <StarIcon className="h-6 w-6" />,
        },
      ],
    },
  ]
}

const sectionGridClass = (count: number) =>
  clsx(
    'grid gap-3 sm:gap-4',
    count === 1 && 'grid-cols-1 max-w-md',
    count === 2 && 'grid-cols-1 sm:grid-cols-2',
    count === 3 && 'grid-cols-1 sm:grid-cols-3',
    count >= 4 && 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'
  )

interface QGNumerosCarouselProps {
  sections: KpiSection[]
  embedded?: boolean
  footer?: React.ReactNode
}

const QGNumerosCarousel: React.FC<QGNumerosCarouselProps> = ({
  sections,
  embedded = false,
  footer,
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  const goTo = useCallback(
    (index: number) => {
      const total = sections.length
      setActiveIndex(((index % total) + total) % total)
    },
    [sections.length]
  )

  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo])
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo])

  useEffect(() => {
    if (paused || sections.length <= 1) return
    const id = window.setInterval(() => goTo(activeIndex + 1), AUTO_ADVANCE_MS)
    return () => clearInterval(id)
  }, [activeIndex, goTo, paused, sections.length])

  const activeSection = sections[activeIndex]

  if (embedded) {
    return <QGNumerosEmbeddedAll sections={sections} footer={footer} />
  }

  return (
    <div
      className={clsx(cardSurface, 'p-3 sm:p-4')}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setPaused(false)
        }
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={goPrev}
          className="flex-shrink-0 rounded-lg p-1.5 border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
          aria-label="Seção anterior"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>

        <div
          className="flex-1 min-w-0 overflow-x-auto overscroll-x-contain scrollbar-thin"
          role="tablist"
          aria-label="Módulos do PASI QG"
        >
          <div className="flex gap-1.5 min-w-max pr-1">
            {sections.map((section, index) => (
              <button
                key={section.id}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                data-active={index === activeIndex}
                onClick={() => goTo(index)}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-full border',
                  'px-3 py-1.5 text-xs font-medium',
                  'transition-colors duration-200 whitespace-nowrap',
                  tabClass
                )}
              >
                <span className="flex-shrink-0 opacity-80">{section.icon}</span>
                {section.title}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={goNext}
          className="flex-shrink-0 rounded-lg p-1.5 border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
          aria-label="Próxima seção"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="overflow-hidden mt-1">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeSection.id}
            role="tabpanel"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className={sectionGridClass(activeSection.items.length)}>
              {activeSection.items.map((item) => (
                <KpiTile key={item.label} item={item} animate />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

interface QGNumerosSectionProps {
  data: QgNumerosRow | null
  loading: boolean
  error: string | null
  embedded?: boolean
}

const QGNumerosSection: React.FC<QGNumerosSectionProps> = ({
  data,
  loading,
  error,
  embedded = false,
}) => {
  const sections = useMemo(() => (data ? buildSections(data) : []), [data])

  const updatedAtLabel =
    data?.updated_at && !loading && !error ? (
      <Text
        size="xs"
        color="muted"
        className={
          embedded
            ? 'px-3 py-2 border-t border-gray-200 dark:border-gray-600 text-center sm:text-right'
            : 'text-right whitespace-nowrap'
        }
      >
        Atualizado em{' '}
        {new Date(data.updated_at).toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </Text>
    ) : undefined

  const body = (
    <>
      {loading && embedded && (
        <ul className={clsx(informativoPanelClass, 'divide-y divide-gray-200 dark:divide-gray-600')}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <li key={i} className="px-4 py-3">
              <DashboardCardSkeleton className="h-14" />
            </li>
          ))}
        </ul>
      )}

      {loading && !embedded && (
        <div className={clsx(cardSurface, 'p-4')}>
          <div className="flex gap-2 mb-4 overflow-hidden">
            <DashboardCardSkeleton className="h-8 w-24 rounded-full flex-shrink-0" />
            <DashboardCardSkeleton className="h-8 w-28 rounded-full flex-shrink-0" />
            <DashboardCardSkeleton className="h-8 w-20 rounded-full flex-shrink-0" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <DashboardCardSkeleton className="h-28 rounded-xl" />
            <DashboardCardSkeleton className="h-28 rounded-xl" />
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-4">
          <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
            {error}
          </Text>
        </div>
      )}

      {!loading && !error && data && sections.length > 0 && (
        <QGNumerosCarousel
          sections={sections}
          embedded={embedded}
          footer={embedded ? updatedAtLabel : undefined}
        />
      )}
    </>
  )

  if (embedded) {
    return <div className="mb-0">{body}</div>
  }

  return (
    <section className="mb-0">
      <DashboardSectionHeader
        compact
        icon={<ChartBarSquareIcon className="h-5 w-5" />}
        title="Números do PASI QG"
        action={updatedAtLabel}
      />
      {body}
    </section>
  )
}

export default QGNumerosSection
